import {
  MessageAttributeValue,
  SendMessageCommand,
  SQSClient,
} from "@aws-sdk/client-sqs"
import { DbTransaction } from "@tc/middy-decorators"
import { eq, and, inArray } from "drizzle-orm"
import {
  partyUser,
  partyUserRoleConstraint,
  partyAgency,
  partyB2GProfile,
} from "@tc/drizzle-v2"

const sqsClient = new SQSClient({})
const queueUrl = process.env["OCA_USERS_QUEUE_URL"]

export interface OcaUserDoc {
  id: string
  subId: string
  systemUsername?: string | null
  fullName: string
  email?: string | null
  accountType: string
  status: string
  jobTitle?: string | null
  contactNumber?: string | null
  certificateTypes: string[]
  countries: string[]
  inspectionPorts: string[]
  agencyId?: string | null
  agencyName?: string | null
  profileName?: string | null
  reason?: string | null
  comment?: string | null
  createdAt: number
  updatedAt: number
  createdBy?: string | null
  updatedBy?: string | null
}

type UserRow = {
  id: string
  sub_id: string | null
  systemUsername: string | null
  name: string | null
  email: string | null
  accountType: string | null
  status: string | null
  jobTitle: string | null
  contactNumber: string | null
  statusReason: string | null
  statusComment: string | null
  createdAt: Date
  updatedAt: Date
  createdBy: string | null
  updatedBy: string | null
  g2gUser: boolean | null
}

type ConstraintRow = {
  userId: string
  constraintType: string
  constraintValue: string
  agencyId: string | null
}

/** Build OcaUserDoc from raw data */
const buildOcaUserDoc = (
  user: UserRow,
  constraints: ConstraintRow[],
  agencyName: string | null,
  profileName: string | null,
): OcaUserDoc => {
  const certificateTypes = constraints
    .filter((c) => c.constraintType === "CERT_TYPE")
    .map((c) => c.constraintValue)

  const countries = constraints
    .filter((c) => c.constraintType === "COUNTRY")
    .map((c) => c.constraintValue)

  const inspectionPorts = constraints
    .filter((c) => c.constraintType === "PORT")
    .map((c) => c.constraintValue)

  const agencyConstraint = constraints.find((c) => c.constraintType === "AGENCY")
  const agencyId =
    agencyConstraint?.agencyId ?? agencyConstraint?.constraintValue ?? null

  return {
    id: user.id,
    subId: user.sub_id ?? "",
    systemUsername: user.systemUsername ?? null,
    fullName: user.name ?? "",
    email: user.email ?? null,
    accountType: user.accountType ?? "",
    status: user.status ?? "",
    jobTitle: user.jobTitle ?? null,
    contactNumber: user.contactNumber ?? null,
    certificateTypes,
    countries,
    inspectionPorts,
    agencyId,
    agencyName,
    profileName,
    reason: user.statusReason ?? null,
    comment: user.statusComment ?? null,
    createdAt: user.createdAt.getTime(),
    updatedAt: user.updatedAt.getTime(),
    createdBy: user.createdBy ?? null,
    updatedBy: user.updatedBy ?? null,
  }
}

export const getOcaUserData = async (
  tx: DbTransaction,
  userId: string,
): Promise<OcaUserDoc | undefined> => {
  const [user] = await tx
    .select()
    .from(partyUser)
    .where(eq(partyUser.id, userId))

  if (!user || !user.g2gUser) {
    return undefined
  }

  // Skip users without valid accountType or status — these are non-G2G users
  // that have g2g_user=true but were never fully set up
  if (!user.accountType || !user.status) {
    return undefined
  }

  // Fetch OCA constraints (multiple rows)
  const constraints = await tx
    .select({
      userId: partyUserRoleConstraint.userId,
      constraintType: partyUserRoleConstraint.constraintType,
      constraintValue: partyUserRoleConstraint.constraintValue,
      agencyId: partyUserRoleConstraint.agencyId,
    })
    .from(partyUserRoleConstraint)
    .where(
      and(
        eq(partyUserRoleConstraint.userId, userId),
        eq(partyUserRoleConstraint.role, "OCA"),
        eq(partyUserRoleConstraint.active, true),
      ),
    )

  const agencyConstraint = constraints.find(
    (c) => c.constraintType === "AGENCY",
  )
  const agencyId =
    agencyConstraint?.agencyId ?? agencyConstraint?.constraintValue ?? null

  let agencyName: string | null = null
  if (agencyId) {
    const [agency] = await tx
      .select({ displayName: partyAgency.displayName })
      .from(partyAgency)
      .where(eq(partyAgency.id, agencyId))
    agencyName = agency?.displayName ?? null
  }

  // Fetch active profile name from b2g_profilename table
  const [profileRow] = await tx
    .select({ profileName: partyB2GProfile.profileName })
    .from(partyB2GProfile)
    .where(
      and(eq(partyB2GProfile.userId, userId), eq(partyB2GProfile.active, true)),
    )
  const profileName = profileRow?.profileName ?? null

  return buildOcaUserDoc(user as UserRow, constraints, agencyName, profileName)
}

/** Bulk fetch — batch queries for a chunk of IDs, returns Map<id, OcaUserDoc> */
export const getOcaUserBulkData = async (
  tx: DbTransaction,
  ids: readonly string[],
): Promise<Map<string, OcaUserDoc>> => {
  if (ids.length === 0) return new Map()

  const BATCH_SIZE = 2000

  // 1. Batch fetch all user rows
  const userRows: UserRow[] = []
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const batch = ids.slice(i, i + BATCH_SIZE) as string[]
    const batchRows = await tx
      .select()
      .from(partyUser)
      .where(inArray(partyUser.id, batch))
    userRows.push(...(batchRows as UserRow[]))
  }

  // Filter to only G2G users with valid accountType and status
  const validUsers = userRows.filter(
    (u) => u.g2gUser && u.accountType && u.status,
  )
  if (validUsers.length === 0) return new Map()

  const validUserIds = validUsers.map((u) => u.id)

  // 2. Batch fetch all OCA constraints for these users
  const constraintRows: ConstraintRow[] = []
  for (let i = 0; i < validUserIds.length; i += BATCH_SIZE) {
    const batch = validUserIds.slice(i, i + BATCH_SIZE)
    const batchRows = await tx
      .select({
        userId: partyUserRoleConstraint.userId,
        constraintType: partyUserRoleConstraint.constraintType,
        constraintValue: partyUserRoleConstraint.constraintValue,
        agencyId: partyUserRoleConstraint.agencyId,
      })
      .from(partyUserRoleConstraint)
      .where(
        and(
          inArray(partyUserRoleConstraint.userId, batch),
          eq(partyUserRoleConstraint.role, "OCA"),
          eq(partyUserRoleConstraint.active, true),
        ),
      )
    constraintRows.push(...batchRows)
  }

  // Group constraints by user ID
  const constraintsByUser = new Map<string, ConstraintRow[]>()
  for (const row of constraintRows) {
    const list = constraintsByUser.get(row.userId) ?? []
    list.push(row)
    constraintsByUser.set(row.userId, list)
  }

  // 3. Collect unique agency IDs to fetch names
  const agencyIds = new Set<string>()
  for (const constraints of constraintsByUser.values()) {
    const agencyConstraint = constraints.find((c) => c.constraintType === "AGENCY")
    const agencyId = agencyConstraint?.agencyId ?? agencyConstraint?.constraintValue
    if (agencyId) agencyIds.add(agencyId)
  }

  // 4. Batch fetch agency names
  const agencyNameById = new Map<string, string>()
  if (agencyIds.size > 0) {
    const agencyIdList = [...agencyIds]
    for (let i = 0; i < agencyIdList.length; i += BATCH_SIZE) {
      const batch = agencyIdList.slice(i, i + BATCH_SIZE)
      const batchRows = await tx
        .select({ id: partyAgency.id, displayName: partyAgency.displayName })
        .from(partyAgency)
        .where(inArray(partyAgency.id, batch))
      for (const row of batchRows) {
        if (row.displayName) agencyNameById.set(row.id, row.displayName)
      }
    }
  }

  // 5. Batch fetch active profile names
  const profileNameByUser = new Map<string, string>()
  for (let i = 0; i < validUserIds.length; i += BATCH_SIZE) {
    const batch = validUserIds.slice(i, i + BATCH_SIZE)
    const batchRows = await tx
      .select({
        userId: partyB2GProfile.userId,
        profileName: partyB2GProfile.profileName,
      })
      .from(partyB2GProfile)
      .where(
        and(
          inArray(partyB2GProfile.userId, batch),
          eq(partyB2GProfile.active, true),
        ),
      )
    for (const row of batchRows) {
      if (row.profileName) profileNameByUser.set(row.userId, row.profileName)
    }
  }

  // 6. Build docs
  const result = new Map<string, OcaUserDoc>()
  for (const user of validUsers) {
    const constraints = constraintsByUser.get(user.id) ?? []
    const agencyConstraint = constraints.find((c) => c.constraintType === "AGENCY")
    const agencyId = agencyConstraint?.agencyId ?? agencyConstraint?.constraintValue
    const agencyName = agencyId ? (agencyNameById.get(agencyId) ?? null) : null
    const profileName = profileNameByUser.get(user.id) ?? null

    result.set(user.id, buildOcaUserDoc(user, constraints, agencyName, profileName))
  }

  return result
}

export const saveOcaUserToSearch = async (
  tx: DbTransaction,
  userId: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const doc = await getOcaUserData(tx, userId)
  if (!doc) {
    console.error(`Failed to prepare OCA user document for id: ${userId}`)
    return
  }

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName,
      DataType: "String",
    },
  }

  const command = new SendMessageCommand({
    QueueUrl: queueUrl,
    MessageBody: JSON.stringify(doc),
    MessageAttributes: attributes,
  })

  await sqsClient.send(command)
}
